/**
 * Rental Unlocks hook using React Relay
 *
 * Fetches the unlock attempt/success events recorded during a rental,
 * grouped per unlockable hardware unit, plus the list of hardware the
 * rider could unlock during the rental.
 *
 * This is intentionally a separate query from useRentalDetailRelay: a
 * backend error while computing the unlocks calculation nulls out the
 * whole getRental payload, and keeping the fields isolated means such a
 * failure only degrades the Unlocks tab instead of blanking the entire
 * rental detail page.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { IUnlockEventJson } from '../../../../util/unlockEvents';
import type { useRentalUnlocksRelayQuery as UseRentalUnlocksRelayQuery } from './__generated__/useRentalUnlocksRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const RentalUnlocksQuery = graphql`
  query useRentalUnlocksRelayQuery($id: ID!) {
    getRental(id: $id) {
      id
      unlocks
      unlocksSuccessRate
      unlockableHardware {
        id
        oldId
        number
        label
        hardwareType {
          id
          name
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * A single unlock event from the rental `unlocks` Json field.
 * The Json scalar arrives already parsed.
 */
export type RentalUnlockEventJson = IUnlockEventJson;

/**
 * One entry of the rental `unlocks` Json field: the events recorded for a
 * single unlockable hardware unit, oldest first (empty when the unit had
 * no unlock events during the rental).
 */
export interface RentalUnlockGroupJson {
  hardware_id: string;
  unlocks: RentalUnlockEventJson[];
}

/**
 * Hardware the rider could unlock during the rental
 */
export interface UnlockableHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  label?: string | null;
  hardwareType?: {
    id: string;
    name?: string | null;
  } | null;
}

/**
 * Rental unlocks payload from GraphQL
 */
export interface RentalUnlocksGraphQL {
  id: string;
  unlocks?: readonly RentalUnlockGroupJson[] | null;
  /**
   * Percentage (0-100) of unlock attempts during the rental (pooled across
   * all unlockable hardware units) with a success recorded between the
   * backward window before the attempt and the unlock window after it; null
   * when there were no attempts. Queried without arguments so the server
   * defaults (backwardWindow: 2, unlock window: 30) apply.
   */
  unlocksSuccessRate?: number | null;
  unlockableHardware?: readonly UnlockableHardwareGraphQL[] | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook that provides a React Query-like interface for a rental's unlock
 * events. Fetches as soon as it is enabled so the data (and the failed
 * unlock count shown on the Unlocks tab badge) is ready before the user
 * visits the tab.
 *
 * @param rentalId - The rental's GraphQL UUID
 * @param options.enabled - Whether to enable the query
 *
 * @example
 * const { data, isLoading, error } = useRentalUnlocksRelayQuery(rentalId);
 */
export function useRentalUnlocksRelayQuery(
  rentalId: string | undefined,
  options?: {
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<RentalUnlocksGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(rentalId);

  useEffect(() => {
    if (!enabled || !rentalId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<UseRentalUnlocksRelayQuery>(
          relayEnvironment,
          RentalUnlocksQuery,
          { id: rentalId }
        ).toPromise();

        if (cancelled) return;

        setData((result?.getRental as RentalUnlocksGraphQL | null) ?? null);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch rental unlocks')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, rentalId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error, refetch]
  );
}
